import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi } from '../services/api';
import { User } from '../types';

// ===== ContexteAuth =====

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (password: string) => Promise<void>;
  logout: () => Promise<void>;
  verifyGOF: (password: string) => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    void checkAuthStatus();
  }, []);

  const checkAuthStatus = async (): Promise<void> => {
    try {
      const response = await authApi.checkAuth();
      if (response.authenticated) {
        setUser({
          id: 1,
          role: response.role as 'utilisateur' | 'gof' | 'technicentre',
          isGOF: response.is_gof || false,
          canModify: response.can_modify || false,
          technicentreId: response.technicentre_id,
          technicentreName: response.technicentre_name,
        });
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (password: string): Promise<void> => {
    try {
      const response = await authApi.login(password);
      if (response.success) {
        setUser({
          id: 1,
          role: response.role as 'utilisateur' | 'gof' | 'technicentre',
          isGOF: response.is_gof || false,
          canModify: response.can_modify || false,
          technicentreId: response.technicentre_id,
          technicentreName: response.technicentre_name,
        });
      } else {
        throw new Error(response.error || 'Login failed');
      }
    } catch (error: any) {

      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authApi.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout failed:', error);
      setUser(null);
    }
  };

  const verifyGOF = async (password: string): Promise<boolean> => {
    try {
      const response = await authApi.verifyGOF(password);
      if (response.success && user) {
        setUser({
          ...user,
          isGOF: true,
          role: 'gof',
          canModify: true,
        });
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    verifyGOF,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
